/**
 * Branding tag detection: regex, extraction, scoring, and classification.
 *
 * Detection states:
 *   detected     – clear branding template tags found
 *   uncertain    – file could not be reliably inspected
 *   not_detected – inspected successfully, no tags found
 */

// Matches {d.…}, {c.…}, {o.…}, {#…}, {t(…)…}
const BRANDING_TAG_REGEX =
  /\{(?:d\.[^{}]{1,700}|c\.[^{}]{1,700}|o\.[^{}]{1,700}|#[^{}]{1,700}|t\([^{}]{0,700}\)[^{}]{0,700})\}/g

// Known formatter hints that raise confidence
const FORMATTER_HINTS = [
  ':imageFit(',
  ':drop(',
  ':color(',
  ':formatD(',
  ':set(',
  ':ifNE(',
  ':ifNEM(',
  ':show(',
  ':elseShow(',
  ':or(',
  ':ifEQ(',
  ':convCurr(',
  ':formatN(',
  ':aggSum(',
  ':aggCount('
]

/**
 * Extract branding tags from a text string.
 * Returns an array of { tag, hasFormatter } objects.
 */
export function extractMarkers(text) {
  if (!text || typeof text !== 'string') return []

  const matches = []
  let match
  // Reset lastIndex in case regex was used before
  BRANDING_TAG_REGEX.lastIndex = 0

  while ((match = BRANDING_TAG_REGEX.exec(text)) !== null) {
    const tag = match[0]
    const hasFormatter = FORMATTER_HINTS.some((hint) => tag.includes(hint))
    matches.push({ tag, hasFormatter })
  }

  return matches
}

/**
 * Scan a single XML string for branding tags using both raw XML
 * and DOMParser-extracted textContent.
 *
 * @param {string} xmlString – raw XML content
 * @param {string} source    – e.g. 'word/document.xml'
 * @returns {{ markers: Array<{ tag: string, source: string, locationType: string }> }}
 */
export function scanXmlForMarkers(xmlString, source) {
  if (!xmlString) return { markers: [] }

  const markers = []
  const seen = new Set()

  const addMarker = (tag, locationType) => {
    const key = `${tag}|${locationType}`
    if (!seen.has(key)) {
      seen.add(key)
      markers.push({ tag, source, locationType })
    }
  }

  // 1. Scan raw XML
  const rawMatches = extractMarkers(xmlString)
  for (const m of rawMatches) {
    addMarker(m.tag, 'raw_xml')
  }

  // 2. Parse with DOMParser and scan textContent (catches split runs in Word)
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(xmlString, 'application/xml')
    const parseError = doc.querySelector('parsererror')
    if (!parseError) {
      const textContent = doc.documentElement.textContent || ''
      const textMatches = extractMarkers(textContent)
      for (const m of textMatches) {
        addMarker(m.tag, 'text_content')
      }
    }
  } catch {
    // DOMParser failure is non-fatal; raw XML results are still valid
  }

  return { markers }
}

/**
 * Classify detection results based on collected markers.
 *
 * @param {{ markers: Array, fullyInspected: boolean, reason?: string }} params
 * @returns {{ state: 'detected'|'uncertain'|'not_detected', markers: Array, reason: string }}
 */
export function classifyResult({ markers = [], fullyInspected = true, reason = '' }) {
  if (markers.length > 0) {
    const hasFormatter = markers.some((m) => {
      const rawMatches = extractMarkers(m.tag)
      return rawMatches.some((rm) => rm.hasFormatter)
    })

    const result = {
      state: 'detected',
      markers,
      reason: hasFormatter
        ? 'Found branding template tags with known formatters in the uploaded document.'
        : 'Found branding template tags in the uploaded document.'
    }

    return result
  }

  if (!fullyInspected) {
    const result = {
      state: 'uncertain',
      markers: [],
      reason: reason || 'Could not fully inspect the file.'
    }

    return result
  }

  const result = {
    state: 'not_detected',
    markers: [],
    reason: 'No branding template tags were found in this file.'
  }

  return result
}
